/* eslint-disable no-underscore-dangle */
import dayjs from 'dayjs';
import { getLocales } from 'expo-localization';
import get from 'lodash/get';
import mergeWith from 'lodash/mergeWith';
import set from 'lodash/set';

import jsonContent from 'app/assets/content.json';
import { GetContentQuery, Locale, ReservationTextType, Text } from 'app/generated/hygraph';
import { AttractionContent } from 'app/hooks/useAttraction';
import { ProductContent } from 'app/hooks/useProduct';
import type { Store, StoreState } from 'app/hooks/useStore';
import Logger from 'app/services/Logger';
import { isActualObject, isLegacyGetContentQuery, isText } from 'app/utils/guards';

export type JSONContent = {
  [key: string]: Record<Locale, string | string[]> | JSONContent;
};

export type I18nContent = {
  [locale: string]: {
    [key: string]: string | string[];
  };
};

export type TranslationHelper = <T extends string | string[] = string>(
  key: string,
  props?: Record<string, string | number | string[]>
) => T;
export type TemplateHelper = (msg: string, props?: Record<string, string | number>) => string;

const reservationTextGroupKey = 'app_reservations';

export const locales: Record<Locale, { label: string; dateFormat: string }> = {
  en: {
    label: 'English',
    dateFormat: 'M/D/YYYY',
  },
  de: {
    label: 'Deutsch',
    dateFormat: 'YYYY-MM-DD',
  },
  es: {
    label: 'Español',
    dateFormat: 'DD/MM/YYYY',
  },
  fr: {
    label: 'Français',
    dateFormat: 'DD/MM/YYYY',
  },
  it: {
    label: 'Italiano',
    dateFormat: 'DD/MM/YYYY',
  },
  ja: {
    label: '日本人',
    dateFormat: 'YYYY年MM月DD日',
  },
  pt: {
    label: 'Português',
    dateFormat: 'DD/MM/YYYY',
  },
  zh: {
    label: '中文',
    dateFormat: 'YYYY-MM-DD',
  },
};

export const initialContent: I18nContent = (function validateJSONContent() {
  if (!isActualObject(jsonContent)) {
    Logger.error('[I18n] content.json is invalid, will use empty object', { jsonContent });
    return {};
  }

  Object.entries(jsonContent).forEach(([locale, values]) => {
    if (typeof locale !== 'string') {
      Logger.warn('[I18n] found unexpected locale format, will be removed', { locale });
      delete jsonContent[locale];
    } else if (!isActualObject(values)) {
      Logger.warn('[I18n] found unexpected locale values, will be removed', {
        locale,
        values,
      });
      delete jsonContent[locale];
    }
    if (!(locale in locales)) {
      Logger.warn('[I18n] found unexpected locale, will be included', { locale });
    }
  });

  return jsonContent as object as I18nContent;
})();

export function getDeviceLocaleWithoutRegion(): string {
  const languageCode = getLocales()?.[0]?.languageCode ?? 'en';
  return languageCode.toLowerCase();
}

export function getInitialLocale(): Locale {
  const deviceLocale = getDeviceLocaleWithoutRegion();
  const defaultLocale = deviceLocale in locales ? (deviceLocale as Locale) : Locale.En;

  return defaultLocale;
}

/**
 * Creates a state aware helper to return messages by key.
 */
export function createTranslationHelper(
  getState: () => Pick<StoreState, 'locale' | 'content' | 'showContentKeys'>
): TranslationHelper {
  return function t<T extends string | string[] = string>(
    key: string,
    props?: Record<string, string | number | string[] | Locale>
  ): T {
    // eslint-disable-next-line @typescript-eslint/no-shadow
    const { content, locale: $locale, showContentKeys } = getState();
    const locale = (props?.locale || $locale) as Locale;

    const msg = get(content[locale], key) || get(content.en, key);

    if (!msg) {
      // Logger.warn(`unable to find content`, {
      //   key,
      //   locale,
      // });
      return '' as T;
    }

    const prefix = !showContentKeys ? '' : `${locale}: ${key}: `;

    try {
      if (typeof msg === 'string') {
        return (prefix + expandTpl(msg, { locale, content, props })) as T;
      }
      if (Array.isArray(msg)) {
        return msg.map((item) => expandTpl(item, { locale, content, props })) as T;
      }

      Logger.warn('unexpected msg type', {
        key,
        type: typeof msg,
        msg,
        props,
      });

      return (__DEV__ ? `UNEXPECTED_TYPE::${key}` : '') as T;
    } catch (error) {
      Logger.error('unable to expand template', {
        key,
        msg,
        props,
        error,
      });
      return (__DEV__ ? `ERROR_EXPANDING::${msg}` : msg) as T;
    }
  };
}

export type ReservationTextKey = `${ReservationTextType}`;
export type ReservationTextHelper = ReturnType<typeof createReservationTextTranslationHelper>;

export function createReservationTextTranslationHelper(
  t: TranslationHelper,
  {
    attraction,
    product,
  }: {
    attraction?: AttractionContent;
    product?: ProductContent;
  }
) {
  return (
    key: string,
    opts: {
      [k: string]: unknown;
      fallback?: boolean;
      general?: boolean;
      all?: boolean;
    } = {}
  ): string => {
    const { fallback = true, general = false, all = false, ...$props } = opts ?? {};
    const props = Object.entries($props).reduce<Record<string, string | number | string[]>>(
      (acc, [k, val]) => {
        if (typeof val === 'string' || typeof val === 'number' || Array.isArray(val)) acc[k] = val;
        return acc;
      },
      {}
    );

    const attractionSpecific =
      !general && !!attraction && findAttractionReservationText(key, attraction, product, props);
    if (attractionSpecific) return attractionSpecific;

    const productSpecific =
      !general && !!product && findProductReservationText(key, product, props);
    if (productSpecific) return productSpecific;

    const generic = fallback && findGeneralReservationText(key, t, props);
    if (generic) return generic;

    // only fallback to all texts when fallback is requested, general is not requested, and all is specifically requested
    // to avoid accidentally falling back to all texts when we only wanted to look in product, attraction and reservation texts
    const searchAll = fallback && !general && all;
    const anyText = searchAll && t(key, props);
    if (anyText) return anyText;

    return '';
  };
}

export function findAttractionReservationText(
  key: string,
  attraction: AttractionContent | undefined,
  product: ProductContent | undefined,
  props: Record<string, string | number | string[]> = {}
): string | undefined | null {
  const texts = attraction?.reservation?.texts;
  if (!texts) return null;

  let msg;

  // eslint-disable-next-line no-restricted-syntax, no-unreachable-loop
  for (const text of texts) {
    const { value, products } = text;
    const $key = getTextKey(text);

    if ($key === key && typeof value === 'string') {
      const isProductSpecific = product?.key && products?.length > 0;

      // product specific content immediately wins
      if (isProductSpecific) {
        if (products.some(($product) => $product.key === product.key)) {
          msg = value;
          break;
        }
      } else {
        // otherwise store the value and check remaining messages for possible product specific content
        msg = value;
      }
    }
  }

  return msg ? expandTpl(msg, { locale: Locale.En, content: {}, props }) : msg;
}

function findProductReservationText(
  key: string,
  product: ProductContent,
  props: Record<string, string | number | string[]>
): string | undefined | null {
  const texts = product?.reservation?.texts;
  if (!texts) return null;
  const msg = texts.find((text) => getTextKey(text) === key)?.value;
  return msg ? expandTpl(msg, { locale: Locale.En, content: {}, props }) : msg;
}

function findGeneralReservationText(
  key: string,
  t: TranslationHelper,
  props: Record<string, string | number | string[]>
) {
  return t<string>(`reservations.${key}`, props);
}

/**
 * Gets wallet values for addon tickets following a hierarchy:
 * 1. Look for addon-specific keys: {addonContentKey}_wallet_guest_value and {addonContentKey}_wallet_ticket_value in attraction.reservation.texts
 * 2. If not found, look for generic keys: wallet_guest_value and wallet_ticket_value in attraction.reservation.texts
 * 3. If still not found, look in Text Groups from "guestcenter" group: wallet.guest.value and wallet.ticket.value
 */
export function getWalletValuesForAddon({
  attractionContent,
  addonContentKey,
  product,
  t,
}: {
  attractionContent?: AttractionContent;
  addonContentKey?: string;
  product?: ProductContent;
  t: TranslationHelper;
}): { walletGuestValue?: string; walletTicketValue?: string } {
  const result: { walletGuestValue?: string; walletTicketValue?: string } = {};

  // Step 1: Try addon-specific keys in attraction.reservation.texts
  if (attractionContent && addonContentKey) {
    const addonGuestKey = `${addonContentKey}_addon_guest_value`;
    const addonTicketKey = `${addonContentKey}_addon_ticket_value`;

    const addonGuestValue = findAttractionReservationText(
      addonGuestKey,
      attractionContent,
      product,
      {}
    );
    const addonTicketValue = findAttractionReservationText(
      addonTicketKey,
      attractionContent,
      product,
      {}
    );

    if (addonGuestValue) result.walletGuestValue = addonGuestValue;
    if (addonTicketValue) result.walletTicketValue = addonTicketValue;
  }

  // Step 2: If not found, try Text Groups from "guestcenter" group
  if (!result.walletGuestValue || !result.walletTicketValue) {
    if (!result.walletGuestValue) {
      const defaultGuestValue = t<string>('addon_guest_value');
      if (defaultGuestValue !== '') result.walletGuestValue = defaultGuestValue;
    }
    if (!result.walletTicketValue) {
      const defaultTicketValue = t<string>('addon_ticket_value');
      if (defaultTicketValue !== '') result.walletTicketValue = defaultTicketValue;
    }
  }

  return result;
}

/**
 * Creates a state aware helper to expand the provided message.
 */
export function createTemplateHelper(
  getState: () => Pick<Store, 'content' | 'locale'>
): TemplateHelper {
  return function tpl(msg: string, props?: Record<string, string | number>) {
    const { content, locale } = getState();
    return expandTpl(msg, { locale, content, props });
  };
}

export function expandTpl(
  msg: string,
  {
    locale = Locale.En,
    content = {},
    props,
  }: {
    locale?: string;
    content?: Record<string, unknown>;
    props?: Record<string, string | number | string[]>;
  }
): string {
  return msg.replace(/\[\[(.+?)\]\]/g, (_, key) => {
    const hasCustomSeparator = /:.+/.test(key);
    let separator = ',';
    let valueKey = key;

    if (hasCustomSeparator) {
      const [$key, $separator] = key.split(':');
      valueKey = $key;
      separator = $separator;
    }

    let value =
      (props && get(props, valueKey)) ??
      get(content[locale], valueKey) ??
      get(content.en, valueKey);

    if (Array.isArray(value)) {
      value = value.join(separator);
    }

    // if (__DEV__) Logger.debug('expandTemplate', { tpl, param: key, value });
    if (typeof value === 'undefined') {
      return __DEV__ ? `UNDEFINED::${valueKey}` : '';
    }

    return expandTpl(`${value ?? ''}`, { locale, content, props });
  });
}

export function mapRemoteContentToI18n(remoteContent: GetContentQuery): I18nContent {
  if (!remoteContent?.textGroups) return {};

  const i18n: I18nContent = {};

  if (isLegacyGetContentQuery(remoteContent)) {
    remoteContent.generalReservations.forEach(({ generalText, productText, attractionText }) => {
      [...(generalText ?? []), ...(productText ?? []), ...(attractionText ?? [])].forEach(
        ({ type, value, locale }) => {
          set(i18n, [locale, 'reservations', type], value);
        }
      );
      return i18n;
    });
  }

  remoteContent.textGroups.forEach((group) => {
    group.text.forEach((item) => {
      if (group.key === reservationTextGroupKey && 'key' in item && 'value' in item) {
        if (isText(item)) {
          const { key, value, locale } = item;
          i18n[locale] = i18n[locale] || {};
          set(i18n, [locale, 'reservations', key], value);
          return;
        }
      }

      if (isText(item)) {
        const { key, value, locale } = item;
        i18n[locale] = i18n[locale] || {};
        if (value) i18n[locale][key] = value;
        return;
      }

      if ('key' in item && 'texts' in item && Array.isArray(item.texts)) {
        const { key, texts } = item;
        const locale = texts[0]?.locale ?? Locale.En;

        i18n[locale] = i18n[locale] || {};
        i18n[locale][key] = texts.map((t) => t.value).filter(Boolean) as string[];
      }
    });
  });

  return i18n;
}

export function formatDate(date: string | Date, locale: Locale): string {
  return dayjs(date).format(locales[locale].dateFormat || locales.en.dateFormat);
}

// for backwards compatibility with ReservationText
export function getTextKey(text: Pick<Text, 'key' | 'value'>) {
  return text.key ?? (text as unknown as { type: string })?.type;
}

export function backfillEnglishLocalizations<T>(obj: T): T {
  const exploreAndModify = (currentObj: any): any => {
    if (Array.isArray(currentObj)) return currentObj.map((item) => exploreAndModify(item));

    if (currentObj !== null && typeof currentObj === 'object') {
      const newObj = { ...currentObj };
      // eslint-disable-next-line no-prototype-builtins
      if (newObj.hasOwnProperty('localizations') && Array.isArray(newObj.localizations)) {
        const keys = Object.keys(newObj).filter((k) => k !== 'localizations' && k !== 'locale');
        keys.forEach((key) => {
          if (newObj[key] === null) {
            const enLocalization = newObj.localizations.find((loc: any) => loc.locale === 'en');
            newObj[key] = enLocalization[key] ?? null;
          }
        });
      }
      Object.keys(newObj).forEach((key) => {
        newObj[key] = exploreAndModify(newObj[key]);
      });
      return newObj;
    }
    return currentObj;
  };

  return exploreAndModify(obj);
}

export const getLocaleDefaults = <
  T extends {
    locale: Locale;
  }
>({
  base = Locale.En,
  targetLocale,
  localizations = [],
}: {
  base?: Locale;
  targetLocale?: Locale;
  localizations: T[];
}): T => {
  const $default = localizations.find((locale) => locale.locale === base) ?? {};
  const found = localizations.find((locale) => locale.locale === targetLocale) ?? {};
  return mergeWith({}, $default, found, (a, b) => (b === null || b === '' ? a : undefined)) as T;
};
